import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';
import { Option } from 'src/components/SelectBox';
import { CRM_PROVIDER_OPTIONS } from '../components/wizard/WizardContext';
import { useCampaignContext } from './CampaignContext';
import {
  IntegrationKey,
  PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS,
  PROVIDERS_WITH_BOARD_CONFIGURATION,
} from 'src/services/integration-app.service';
import { getSafeReturnToPath } from 'src/utils/returnTo';

const FALLBACK_RETURN_TO_PATH = '/campaigns';

export interface MondayConfig {
  boardId?: string;
  groupId?: string | null;
  boardFields?: Option[];
}

export enum CrmSyncStep {
  configuration = 'configuration',
  mapping = 'mapping',
  preview = 'preview',
}

export const providerSupportsSegmentSelection = (
  provider: IntegrationKey,
): boolean => {
  return PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS.includes(provider);
};

export const providerSupportsBoardConfiguration = (
  provider: IntegrationKey,
): boolean => {
  return PROVIDERS_WITH_BOARD_CONFIGURATION.includes(provider);
};

export const providerNeedsExtraConfiguration = (
  provider: IntegrationKey,
): boolean => {
  return (
    providerSupportsSegmentSelection(provider) ||
    providerSupportsBoardConfiguration(provider)
  );
};

interface ImportError {
  recordDescription: string;
  message: string;
}

interface CrmSyncContextValue {
  currentStep: CrmSyncStep;
  integratedCrm: Option | null;
  segmentId?: string;
  mondayConfig: MondayConfig;
  syncInProgress: boolean;
  importErrors: ImportError[];

  setCurrentStep: (step: CrmSyncStep) => void;
  setIntegratedCrm: (crm: Option | null) => void;
  setSegmentId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setMondayConfig: React.Dispatch<React.SetStateAction<MondayConfig>>;
  setSyncInProgress: (inProgress: boolean) => void;
  setImportErrors: Dispatch<SetStateAction<ImportError[]>>;

  handleStepNavigation: (direction: 'back' | 'continue') => void;
  handleConfigurationComplete: () => void;
  clearErrors: () => void;

  navigateToReturnTo: (options?: NavigateOptions) => void;
}

const CrmSyncContext = createContext<CrmSyncContextValue | undefined>(
  undefined,
);

export const useCrmSync = () => {
  const context = useContext(CrmSyncContext);
  if (context === undefined) {
    throw new Error('useCrmSync must be used within a CrmSyncProvider');
  }
  return context;
};

interface CrmSyncProviderProps {
  children: ReactNode;
}

export const CrmSyncProvider: React.FC<CrmSyncProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { campaign } = useCampaignContext();
  const returnTo = searchParams.get('returnTo');

  const returnToPath = useMemo(() => {
    return getSafeReturnToPath(returnTo, FALLBACK_RETURN_TO_PATH);
  }, [returnTo]);

  const navigateToReturnTo = useCallback(
    (options?: NavigateOptions) => {
      navigate(returnToPath, options);
    },
    [navigate, returnToPath],
  );

  const [currentStep, setCurrentStep] = useState<CrmSyncStep>(
    CrmSyncStep.configuration,
  );
  const [integratedCrm, setIntegratedCrm] = useState<Option | null>(null);
  const [segmentId, setSegmentId] = useState<string | undefined>(undefined);
  const [mondayConfig, setMondayConfig] = useState<MondayConfig>({});
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);

  useEffect(() => {
    if (campaign?.crmCampaignConfig?.provider) {
      const crmOption = CRM_PROVIDER_OPTIONS.find(
        (option) => option.value === campaign?.crmCampaignConfig?.provider,
      );
      if (crmOption) {
        setIntegratedCrm(crmOption);
      }
    }
  }, [campaign]);

  useEffect(() => {
    if (integratedCrm) {
      const needsExtraConfig = providerNeedsExtraConfiguration(
        integratedCrm.value as IntegrationKey,
      );
      if (!needsExtraConfig && currentStep === CrmSyncStep.configuration) {
        setCurrentStep(CrmSyncStep.mapping);
      }
    }
  }, [integratedCrm, currentStep]);

  const handleStepNavigation = (direction: 'back' | 'continue') => {
    if (direction === 'back') {
      if (currentStep === CrmSyncStep.preview) {
        setCurrentStep(CrmSyncStep.mapping);
      } else if (currentStep === CrmSyncStep.mapping) {
        const needsExtraConfig = integratedCrm
          ? providerNeedsExtraConfiguration(
              integratedCrm.value as IntegrationKey,
            )
          : false;
        if (needsExtraConfig) {
          setCurrentStep(CrmSyncStep.configuration);
        } else {
          navigateToReturnTo();
        }
      } else {
        navigateToReturnTo();
      }
    } else {
      if (currentStep === CrmSyncStep.configuration) {
        setCurrentStep(CrmSyncStep.mapping);
      } else if (currentStep === CrmSyncStep.mapping) {
        setCurrentStep(CrmSyncStep.preview);
      }
    }
  };

  const handleConfigurationComplete = () => {
    setCurrentStep(CrmSyncStep.mapping);
  };

  const clearErrors = () => {
    setImportErrors([]);
  };

  const value: CrmSyncContextValue = {
    currentStep,
    integratedCrm,
    segmentId,
    mondayConfig,
    syncInProgress,
    importErrors,

    setCurrentStep,
    setIntegratedCrm,
    setSegmentId,
    setMondayConfig,
    setSyncInProgress,
    setImportErrors,

    handleStepNavigation,
    handleConfigurationComplete,
    clearErrors,

    navigateToReturnTo,
  };

  return (
    <CrmSyncContext.Provider value={value}>{children}</CrmSyncContext.Provider>
  );
};

export type { ImportError };
